import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import "../UploadComplete/UploadComplete.scss";

import BG from "../../Assets/upload-bg.png";
import TICK from "../../Assets/tick.png";
import BGG from "../../Assets/reversebid.png";

import { apiRequest } from "../../Api/Api";
import useSocketLiveRefresh from "../../hooks/useSocketLiveRefresh";
import PatientFlowStepper, {
  buildPatientFlowSteps,
} from "../../components/PatientFlowStepper/PatientFlowStepper";
import { useTranslation } from "react-i18next";
import PatientAppBar from "../../components/PatientAppBar/PatientAppBar";
import { usePatientSubpageBack } from "../../hooks/usePatientSubpageBack";

const approvedStatuses = [
  "approved",
  "accepted",
  "verified",
  "completed",
  "complete",
  "active",
  "success",
];

const pendingStatuses = [
  "pending",
  "under_review",
  "under-review",
  "review",
  "processing",
  "uploaded",
  "submitted",
  "in_progress",
  "in-progress",
];

const asArray = (value) => (Array.isArray(value) ? value : []);

const pickItems = (payload) => {
  return [
    ...asArray(payload),
    ...asArray(payload?.items),
    ...asArray(payload?.data),
    ...asArray(payload?.documents),
    ...asArray(payload?.prescriptions),
    ...asArray(payload?.reports),
    ...asArray(payload?.uploads),
    ...asArray(payload?.rows),
    ...asArray(payload?.result),
    ...asArray(payload?.results),
    ...asArray(payload?.data?.items),
    ...asArray(payload?.data?.data),
    ...asArray(payload?.data?.documents),
    ...asArray(payload?.data?.prescriptions),
    ...asArray(payload?.data?.reports),
    ...asArray(payload?.data?.uploads),
    ...asArray(payload?.data?.rows),
    ...asArray(payload?.data?.result),
    ...asArray(payload?.data?.results),
  ].filter(Boolean);
};

const readStatus = (item) =>
  String(
    item?.status ||
      item?.approvalStatus ||
      item?.verificationStatus ||
      item?.adminStatus ||
      item?.state ||
      item?.documentStatus ||
      item?.fileStatus ||
      item?.reviewStatus ||
      "",
  )
    .trim()
    .toLowerCase();

const hasFileData = (item) =>
  Boolean(
    item?.url ||
      item?.fileUrl ||
      item?.downloadUrl ||
      item?.key ||
      item?.filename ||
      item?.originalName ||
      item?.name ||
      item?._id ||
      item?.id,
  );

const normalizeDocType = (item) => {
  const raw = String(item?.type || item?.documentType || "").toLowerCase();
  if (raw.includes("report")) return "report";
  if (raw.includes("insurance")) return "insurance";
  return "prescription";
};

const normalizeDoc = (item) => {
  const id = item?._id || item?.id;
  const status = readStatus(item) || "pending";
  const type = normalizeDocType(item);
  const name =
    item?.originalName ||
    item?.filename ||
    item?.fileName ||
    item?.name ||
    (type === "report"
      ? "Medical report"
      : type === "insurance"
        ? "Insurance document"
        : "Prescription");
  const rejectionReason =
    item?.rejectionReason ||
    item?.reason ||
    item?.rejectReason ||
    item?.rejection_message ||
    "";
  return { id, status, type, name, rejectionReason };
};

const getDocs = (payload) => {
  const items = pickItems(payload).filter(hasFileData);
  const docs = items.map(normalizeDoc).filter((d) => d.id);
  const map = new Map();
  docs.forEach((d) => map.set(String(d.id), d));
  return Array.from(map.values());
};

const MAX_FILE_BYTES = 20 * 1024 * 1024;

const getApprovalState = (payload) => {
  const items = pickItems(payload);

  if (!items.length) {
    return {
      approved: false,
      pending: false,
      rejected: false,
      label: "No upload found yet.",
    };
  }

  let approved = false;
  let pending = false;
  let rejected = false;
  let hasAnyUpload = false;

  items.forEach((item) => {
    const status = readStatus(item);

    if (status) {
      if (approvedStatuses.includes(status)) approved = true;
      else if (pendingStatuses.includes(status)) pending = true;
      else if (status.includes("reject")) rejected = true;
    }

    if (hasFileData(item)) {
      hasAnyUpload = true;
    }
  });

  if (!approved && !pending && !rejected && hasAnyUpload) {
    pending = true;
  }

  let label = "Waiting for admin approval...";
  if (approved) label = "Approved by admin.";
  else if (rejected) label = "Some documents were rejected. Please review.";
  else if (pending) label = "We’re checking your documents — usually within 2–4 hours. We’ll notify you when you can share your budget.";

  return { approved, pending, rejected, label };
};

const getSelectionList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.selections)) return payload.selections;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

export default function PendingApproval() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const fromCarePrice = location.state?.from === "/care-price";
  const { backTo, backLabel } = usePatientSubpageBack({
    to: fromCarePrice ? "/HealBidDashboard" : "/request/new",
    label: fromCarePrice ? "Dashboard" : "New request",
  });

  const [checkingApproval, setCheckingApproval] = useState(true);
  const [approvalPayload, setApprovalPayload] = useState(null);
  const [mePayload, setMePayload] = useState(null);
  const [approvalError, setApprovalError] = useState("");
  const [manualChecking, setManualChecking] = useState(false);
  const [reuploadingId, setReuploadingId] = useState(null);
  const [reuploadError, setReuploadError] = useState("");
  const [hasCategorySelection, setHasCategorySelection] = useState(true);
  const fileInputRefs = useRef({});

  const steps = useMemo(() => buildPatientFlowSteps(t), [t]);

  const approvalState = useMemo(() => {
    return getApprovalState(approvalPayload);
  }, [approvalPayload]);

  const docs = useMemo(() => getDocs(approvalPayload), [approvalPayload]);
  const accountStatus = String(
    mePayload?.user?.adminApprovalStatus || "",
  ).toLowerCase();
  const accountRejected = accountStatus === "rejected";
  const accountApproved = accountStatus === "approved";
  const accountPending = !accountApproved && !accountRejected;
  const accountRejectionReason = mePayload?.user?.rejectionReason || "";
  const verificationMessage =
    approvalPayload?.verificationMessage || approvalPayload?.message || "";

  const checkApproval = useCallback(
    async (silent = false) => {
      try {
        if (!silent) setCheckingApproval(true);

        setApprovalError("");

        const [presRes, meRes, selRes] = await Promise.allSettled([
          apiRequest({
            method: "GET",
            url: "/app/prescriptions",
            params: { _t: Date.now() },
            headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
          }),
          apiRequest({
            method: "GET",
            url: "/app/me",
            params: { _t: Date.now() },
            headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
          }),
          apiRequest({
            method: "GET",
            url: "/app/selections",
            params: { _t: Date.now() },
            headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
          }),
        ]);

        const normalizedPayload =
          presRes.status === "fulfilled"
            ? presRes.value?.data || presRes.value || {}
            : {};
        const normalizedMe =
          meRes.status === "fulfilled"
            ? meRes.value?.data || meRes.value || {}
            : null;

        if (selRes.status === "fulfilled") {
          const raw = selRes.value?.data || selRes.value || {};
          const list = getSelectionList(raw);
          setHasCategorySelection(Array.isArray(list) && list.length > 0);
        }

        if (presRes.status === "fulfilled") {
          setApprovalPayload(normalizedPayload);
        }
        if (meRes.status === "fulfilled") {
          setMePayload(normalizedMe);
        }

        const acct = String(
          normalizedMe?.user?.adminApprovalStatus || "",
        ).toLowerCase();

        if (acct === "approved") {
          navigate("/care-price", {
            replace: true,
            state: location.state || undefined,
          });
        }
      } catch (err) {
        console.error("approval check error =>", err);
        setApprovalError(err?.message || "Failed to check approval status");
      } finally {
        if (!silent) setCheckingApproval(false);
      }
    },
    [navigate, location.state],
  );

  useEffect(() => {
    checkApproval(false);
  }, [checkApproval]);

  useSocketLiveRefresh({
    enabled: true,
    role: "patient",
    events: ["notification:new", "me:refresh"],
    onEvent: (payload) => {
      const t = String(payload?.type || "").toLowerCase();
      if (t === "chat_message" || t === "chat_reply") return;
      checkApproval(true);
    },
    pollMs: 7000,
  });

  useEffect(() => {
    const onGlobalRefresh = (e) => {
      const type = String(e?.detail?.type || "").toLowerCase();
      if (type === "me_refresh" || type === "patient_account_approved") {
        checkApproval(true);
      }
    };
    window.addEventListener("healbid:patient-notification", onGlobalRefresh);
    return () => window.removeEventListener("healbid:patient-notification", onGlobalRefresh);
  }, [checkApproval]);

  const handleCheckAgain = async () => {
    try {
      setManualChecking(true);
      await checkApproval(false);
    } finally {
      setManualChecking(false);
    }
  };

  const triggerReuploadPicker = (docId) => {
    const key = String(docId);
    const input = fileInputRefs.current[key];
    if (input) input.click();
  };

  const handleReuploadFileSelected = async (doc, file) => {
    if (!doc?.id || !file) return;
    if (file.size > MAX_FILE_BYTES) {
      setReuploadError("File too large. Please upload a file up to 20 MB.");
      return;
    }
    setReuploadError("");
    setReuploadingId(String(doc.id));
    try {
      const metaRes = await apiRequest({
        method: "POST",
        url: `/app/prescriptions/${doc.id}/upload-url`,
        data: { filename: file.name },
      });
      const meta = metaRes?.data || metaRes || {};
      if (!meta.uploadUrl || !meta.key)
        throw new Error("Upload URL not received");

      const putRes = await fetch(meta.uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
        body: file,
      });
      if (!putRes.ok) throw new Error("Failed to upload file");

      await apiRequest({
        method: "PUT",
        url: `/app/prescriptions/${doc.id}`,
        data: { key: meta.key, originalName: file.name },
      });

      setReuploadError("");
      await checkApproval(false);
    } catch (err) {
      console.error("reupload error =>", err);
      setReuploadError(err?.message || "Re-upload failed");
    } finally {
      setReuploadingId(null);
    }
  };

  const handleStepClick = (stepIndex) => {
    const s = location.state;
    if (stepIndex >= 0 && stepIndex <= 2) navigate("/request/new", { state: s });
    if (stepIndex === 3) navigate("/request/new", { state: s });
  };

  const typeLabel = (type) => {
    if (type === "report") return "Medical report";
    if (type === "insurance") return "Insurance";
    return "Prescription";
  };

  return (
    <section className="updone" style={{ backgroundImage: `url(${BG})` }}>
      <PatientAppBar
        title="Approval status"
        subtitle="We’ll notify you when your account is ready for care pricing."
        backTo={backTo}
        backLabel={backLabel}
      />
      <div className="updone__panel" style={{ backgroundImage: `url(${BGG})` }}>
        <PatientFlowStepper
          steps={steps}
          activeStep={4}
          onStepClick={handleStepClick}
          extraClass="updoneStepper"
        />

        <div className="updone__content">
          <div className="updone__tickWrap">
            <img className="updone__tick" src={TICK} alt="Awaiting approval" />
          </div>

          <h2 className="updone__title">Admin approval</h2>

          {fromCarePrice ? (
            <p
              className="updone__sub"
              style={{
                background: "rgba(11, 129, 123, 0.08)",
                borderRadius: 12,
                padding: "12px 14px",
                border: "1px solid rgba(11, 129, 123, 0.2)",
              }}
              role="status"
            >
              <strong>Care pricing</strong> unlocks after your account is approved. We redirected you here so you
              can track verification status. Use <strong>Dashboard</strong> above to return to your home view anytime.
            </p>
          ) : null}

          <p className="updone__sub">
            Your category is saved. An administrator will review your profile and documents
            before you can continue to bids.
          </p>

          {!hasCategorySelection && (
            <p className="updone__note" style={{ color: "#ff4d4f", marginTop: "12px" }}>
              No treatment category selected yet.{" "}
              <Link
                to="/request/new"
                state={location.state}
                style={{ color: "#0b817b", fontWeight: 700 }}
              >
                Select a category
              </Link>
            </p>
          )}

          <p className="updone__note" style={{ marginTop: "14px" }}>
            {checkingApproval
              ? "Checking approval status..."
              : accountRejected
                ? "Your account was rejected. Please review the reason below."
                : accountApproved
                  ? "Approved by admin. Redirecting you to the bid step..."
                  : "Your account is pending admin approval."}
          </p>

          {accountRejected && (
            <p
              className="updone__note"
              style={{ color: "#ff4d4f", marginTop: "10px" }}
            >
              Reason: {accountRejectionReason || "Rejected by admin."}
            </p>
          )}

          {accountPending && verificationMessage && (
            <p
              className="updone__note"
              style={{ color: "#0b817b", marginTop: "10px" }}
            >
              {verificationMessage}
            </p>
          )}

          {approvalError && (
            <p
              className="updone__note"
              style={{ color: "#ff4d4f", marginTop: "8px" }}
            >
              {approvalError}
            </p>
          )}

          {reuploadError && (
            <p
              className="updone__note"
              style={{ color: "#ff4d4f", marginTop: "10px" }}
            >
              {reuploadError}
            </p>
          )}

          {approvalState.rejected && docs.length > 0 && (
            <div className="updocs">
              <h3 className="updocs__title">Rejected documents</h3>
              <div className="updocs__list">
                {docs
                  .filter((d) =>
                    String(d.status).toLowerCase().includes("reject"),
                  )
                  .map((d) => (
                    <div key={String(d.id)} className="updocs__row isRejected">
                      <div className="updocs__main">
                        <div className="updocs__name" title={d.name}>
                          {d.name}
                        </div>
                        <div className="updocs__meta">
                          <span className="updocs__badge bad">Rejected</span>
                          <span className="updocs__type">{typeLabel(d.type)}</span>
                        </div>
                        {reuploadingId !== String(d.id) && (
                          <div className="updocs__reason">
                            Reason:{" "}
                            {d.rejectionReason ||
                              "Rejected by admin. Please re-upload."}
                          </div>
                        )}
                      </div>

                      <div className="updocs__actions">
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png,.pdf"
                          style={{ display: "none" }}
                          ref={(el) => {
                            if (el) fileInputRefs.current[String(d.id)] = el;
                          }}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            e.target.value = "";
                            handleReuploadFileSelected(d, file);
                          }}
                        />
                        <button
                          type="button"
                          className="updocs__reupload"
                          onClick={() => triggerReuploadPicker(d.id)}
                          disabled={reuploadingId === String(d.id)}
                        >
                          {reuploadingId === String(d.id)
                            ? "Re-uploading..."
                            : "Re-upload"}
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {accountPending && !approvalState.rejected && (
            <button
              type="button"
              onClick={handleCheckAgain}
              disabled={manualChecking || checkingApproval}
              style={{
                marginTop: "18px",
                padding: "12px 20px",
                borderRadius: "10px",
                border: "none",
                cursor:
                  manualChecking || checkingApproval
                    ? "not-allowed"
                    : "pointer",
                fontWeight: 700,
                opacity: manualChecking || checkingApproval ? 0.7 : 1,
              }}
            >
              {manualChecking || checkingApproval
                ? "Checking..."
                : "Check Again"}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
